/**
 * handleApiResponseErrors
 *
 * This function handles errors from API responses by checking for error codes and validation errors.
 * It processes the response and throws an error with a relevant message if there are issues.
 * It supports validation errors, general status errors, and redirects for specific error codes.
 *
 * Features:
 * - Checks for validation errors and formats them into a readable error message.
 * - Throws a specific error message based on the API response.
 * - Redirects to specific pages based on error codes.
 * - Supports translations for error messages.
 *
 * Dependencies:
 * - Functions: `getTranslation`, `ErrorCodesRedirections`
 * - Types: `GeneralResponse`
 *
 * @param response - The API response object, which should conform to the `GeneralResponse<T>` type.
 * @param errorKey - A string used to retrieve a translation key for the general error message.
 *
 * @throws {Error} Throws an error with the formatted message based on the response.
 *
 * Example usage:
 * ```ts
 * handleApiResponseErrors(response, "some_error_key");
 * ```
 *
 * Author: DomeCare Dev Team
 * Created: 2025-06-02
 */

import { GeneralResponse } from "../../types/api";
import { ErrorCodesRedirections, getTranslation } from "../Functions";

// -------------------------
// handleApiResponseErrors Function
// -------------------------

export const handleApiResponseErrors = <T>(
  response: GeneralResponse<T>,
  errorKey: string,
): void => {
  if (response.Code === -1) {
    const validationErrors: any = response.Data || [];
    const errorMessages = validationErrors
      .map((error: any) => {
        return `${getTranslation(error.Key)}: ${error.Value}`;
      })
      .join(",");
    throw new Error(getTranslation(errorKey) + ": " + errorMessages);
  } else if (!response.Status) {
    ErrorCodesRedirections(response.Code);
    throw new Error(
      response.ErrorMessage || getTranslation("unknown_error_occurred"),
    );
  }
};
